import { ensureOk } from "@/api/apiError";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { httpClient } from "@/api/httpClient";
import type { components } from "@/api/generated-types";
import type { AdminGroupDetail } from "@/types";
import type { AdminListQuery } from "./useAdminUsers";

export type PaginatedAdminGroups =
  components["schemas"]["PaginatedResponse_AdminGroup_"];

function buildQueryString(params: AdminListQuery): string {
  const search = new URLSearchParams();
  if (params.q) search.set("q", params.q);
  search.set("skip", String(params.skip ?? 0));
  search.set("limit", String(params.limit ?? 20));
  return `?${search.toString()}`;
}

async function fetchAdminGroups(
  params: AdminListQuery,
): Promise<PaginatedAdminGroups> {
  const response = await httpClient(`/admin/groups${buildQueryString(params)}`);
  await ensureOk(response);
  return response.json() as Promise<PaginatedAdminGroups>;
}

async function fetchAdminGroup(name: string): Promise<AdminGroupDetail> {
  const response = await httpClient(
    `/admin/groups/${encodeURIComponent(name)}`,
  );
  await ensureOk(response);
  return response.json() as Promise<AdminGroupDetail>;
}

/** US-10: admin listing of all in-app RBAC groups (search + pagination server-side). */
export function useAdminGroups(params: AdminListQuery = {}) {
  return useQuery({
    queryKey: ["admin", "groups", params],
    queryFn: () => fetchAdminGroups(params),
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
  });
}

export function useAdminGroup(name: string | undefined) {
  return useQuery({
    queryKey: ["admin", "groups", name],
    queryFn: () => fetchAdminGroup(name as string),
    enabled: Boolean(name),
    staleTime: 30 * 1000,
  });
}

async function addMember(input: {
  groupName: string;
  userId: string;
}): Promise<AdminGroupDetail> {
  const response = await httpClient(
    `/admin/groups/${encodeURIComponent(input.groupName)}/members/${encodeURIComponent(input.userId)}`,
    { method: "POST" },
  );
  await ensureOk(response);
  return response.json() as Promise<AdminGroupDetail>;
}

async function removeMember(input: {
  groupName: string;
  userId: string;
}): Promise<AdminGroupDetail> {
  const response = await httpClient(
    `/admin/groups/${encodeURIComponent(input.groupName)}/members/${encodeURIComponent(input.userId)}`,
    { method: "DELETE" },
  );
  await ensureOk(response);
  return response.json() as Promise<AdminGroupDetail>;
}

/** Add/remove a user from a group (admin membership management, US-10). Invalidates both the
 * group detail and the admin users list (a user's `groups` field changes too). */
export function useAddGroupMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addMember,
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["admin", "groups", variables.groupName],
      });
      void queryClient.invalidateQueries({ queryKey: ["admin", "groups"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

export function useRemoveGroupMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeMember,
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["admin", "groups", variables.groupName],
      });
      void queryClient.invalidateQueries({ queryKey: ["admin", "groups"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}
